import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { verifyMagicLinkToken } from "./magicLinkApi";
import { saveGuestSession, loadGuestSession, isGuestSessionLikelyValid } from "./guestSession";
import { ensureGuestMpcWallet } from "./guestMpc";
import { isIosDevice, isStandaloneDisplay, writeInviteHandoff } from "./inviteHandoff";
function isAndroidDevice() {
    return /Android/i.test(navigator.userAgent || "");
}
const wrapStyle = {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left))",
    textAlign: "center",
    background: "#10131b",
    color: "#e8eaf0",
    fontFamily: "inherit",
};
const primaryBtnStyle = {
    background: "#e91e8c",
    border: "none",
    color: "#fff",
    padding: "14px 28px",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
    maxWidth: 340,
    minHeight: 48,
};
const secondaryBtnStyle = {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#e8eaf0",
    padding: "12px 28px",
    borderRadius: 10,
    fontSize: 15,
    cursor: "pointer",
    width: "100%",
    maxWidth: 340,
    minHeight: 48,
    marginTop: 12,
};
/**
 * Landing page for magic-link invitations (/guest-login?token=...).
 * Redeems the one-time token for a guest session, kicks off background MPC
 * wallet creation, then forwards to the shared chat room. On a mobile browser,
 * guests choose whether to hand the unredeemed link to the installed app or
 * continue in the browser.
 */
const GuestLoginPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("verifying");
    const [errorKey, setErrorKey] = useState("magicLink.errors.generic");
    const [errorDetail, setErrorDetail] = useState("");
    // Generation counter: bumped on every effect re-run/unmount so that a
    // redemption started under an older generation can never write state,
    // save a session, or navigate after its page instance is gone.
    const genRef = useRef(0);
    const redeem = useCallback(async (token) => {
        const gen = genRef.current;
        const stale = () => genRef.current !== gen;
        setStatus("verifying");
        try {
            const session = await verifyMagicLinkToken(token);
            if (stale())
                return;
            saveGuestSession(session);
            // Built-in MPC wallet: fire-and-forget, chat access doesn't wait for it.
            void ensureGuestMpcWallet(session.sessionJwt);
            navigate(session.scope === "full" ? "/guest-dashboard" : "/guest-chat", { replace: true });
        }
        catch (e) {
            if (stale())
                return;
            // If the link was already used but this browser still has a valid session, let them in.
            if (e?.code === "already_used" && isGuestSessionLikelyValid(loadGuestSession())) {
                const sess = loadGuestSession();
                navigate(sess?.scope === "full" ? "/guest-dashboard" : "/guest-chat", { replace: true });
                return;
            }
            switch (e?.code) {
                case "not_yet_valid":
                    setErrorKey("magicLink.errors.notYetValid");
                    if (e?.details?.validFrom) {
                        const start = new Date(e.details.validFrom);
                        if (Number.isFinite(start.getTime())) {
                            const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                            setErrorDetail(`${start.toLocaleString(undefined, { timeZoneName: "short" })} (${zone})`);
                        }
                    }
                    break;
                case "expired":
                    setErrorKey("magicLink.errors.expired");
                    break;
                case "join_window_closed":
                    setErrorKey("magicLink.errors.joinWindowClosed");
                    if (e?.details?.joinUntil) {
                        setErrorDetail(new Date(e.details.joinUntil).toLocaleString());
                    }
                    break;
                case "revoked":
                    setErrorKey("magicLink.errors.revoked");
                    break;
                case "already_used":
                    setErrorKey("magicLink.errors.alreadyUsed");
                    break;
                case "invalid_token":
                    setErrorKey("magicLink.errors.invalid");
                    break;
                case "admin_not_present":
                    setErrorKey("magicLink.errors.adminNotPresent");
                    break;
                default:
                    setErrorKey("magicLink.errors.generic");
            }
            setStatus("error");
        }
    }, [navigate]);
    useEffect(() => {
        const token = searchParams.get("token");
        // Returning guest with a still-valid session and no fresh token → go straight in.
        const existing = loadGuestSession();
        if (!token && isGuestSessionLikelyValid(existing)) {
            navigate(existing?.scope === "full" ? "/guest-dashboard" : "/guest-chat", { replace: true });
            return;
        }
        if (!token) {
            setErrorKey("magicLink.errors.missingToken");
            setStatus("error");
            return;
        }
        if ((isIosDevice() || isAndroidDevice()) && !isStandaloneDisplay()) {
            setStatus("chooser");
            return () => {
                genRef.current += 1;
            };
        }
        void redeem(token);
        // Always invalidate in-flight redemptions when this effect instance dies
        // (unmount, token change) — regardless of which branch ran above.
        return () => {
            genRef.current += 1;
        };
    }, [searchParams, navigate, redeem]);
    const token = searchParams.get("token") || "";
    const handleOpenInApp = async () => {
        try {
            await writeInviteHandoff(token);
            setStatus("copied");
        }
        catch {
            // A blocked clipboard must never trap a guest: use the browser route.
            void redeem(token);
        }
    };
    return (_jsxs("div", { style: wrapStyle, children: [status === "verifying" && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: { width: 42, height: 42, marginBottom: 16, border: "2px solid #e91e8c", borderRadius: "50%", opacity: 0.9 } }), _jsx("h2", { style: { margin: 0 }, children: t("magicLink.guest.verifying") }), _jsx("p", { style: { opacity: 0.7 }, children: t("magicLink.guest.verifyingHint") })] })), status === "chooser" && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: { width: 42, height: 30, marginBottom: 16, border: "2px solid #8ca7c9", borderRadius: 8 } }), _jsx("h2", { style: { margin: 0 }, children: t("magicLink.handoff.title") }), _jsx("p", { style: { opacity: 0.7, marginTop: 8, maxWidth: 360 }, children: t("magicLink.handoff.subtitle") }), _jsx("button", { type: "button", style: { ...primaryBtnStyle, marginTop: 20 }, onClick: () => void redeem(token), children: t("magicLink.handoff.continueInBrowser") }), _jsx("button", { type: "button", style: secondaryBtnStyle, onClick: handleOpenInApp, children: t("magicLink.handoff.copyAndOpenApp") })] })), status === "copied" && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: { width: 42, height: 42, marginBottom: 16, border: "2px solid #72b7a1", borderRadius: "50%" } }), _jsx("h2", { style: { margin: 0 }, children: t("magicLink.handoff.copiedTitle") }), _jsxs("p", { style: { opacity: 0.8, marginTop: 12, maxWidth: 380, lineHeight: 1.5 }, children: [t("magicLink.handoff.copiedStep1"), _jsx("br", {}), t("magicLink.handoff.copiedStep2")] }), _jsx("button", { type: "button", style: secondaryBtnStyle, onClick: () => void redeem(token), children: t("magicLink.handoff.continueInBrowser") })] })), status === "error" && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: { width: 42, height: 42, marginBottom: 16, border: "2px solid #d69a72", borderRadius: 8 } }), _jsx("h2", { style: { margin: 0 }, children: t(errorKey) }), errorDetail && _jsx("p", { style: { opacity: 0.8, marginTop: 8 }, children: errorDetail }), _jsx("p", { style: { opacity: 0.7, marginTop: 12 }, children: t(errorKey === "magicLink.errors.notYetValid"
                            ? "magicLink.errors.notYetValidHint"
                            : "magicLink.errors.askNewInvite") })] }))] }));
};
export default GuestLoginPage;
